import type { ReactNode } from 'react'

import { cn } from 'cn'

/**
 * What a list looks like when there is nothing in it.
 *
 * The console already did this well in words — "No stands are standing in the competition yet.",
 * "Meeting requests are not open yet." — and badly in every other respect: eleven inline
 * `<tr><td colSpan={n}>` or `<p>` blocks with centered muted text and no way to act.
 *
 * **The sentence is kept and the dead end is not.** An empty state that only reports emptiness
 * leaves somebody to work out whether they are looking at a bug, a permission they lack, or a
 * queue they have finished. `action` is optional because some of these genuinely have no next
 * step — nobody can make a delegate request a meeting — and offering a button that does nothing
 * useful is worse than offering none.
 *
 * `icon` is decorative by construction: it is rendered `aria-hidden`, because the heading beside
 * it already says the same thing and a screen reader announcing "inbox graphic, no registrations
 * yet" is announcing it twice.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  /** A lucide icon element. Decorative — the title carries the meaning. */
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      {icon === undefined ? null : (
        <div aria-hidden="true" className="text-muted-foreground [&_svg]:size-8">
          {icon}
        </div>
      )}

      {/* `<p>`, not a heading. This appears inside a section that already has one, and an extra
          `<h2>` per empty list would fill a screen reader's heading outline with absences. */}
      <p className="font-medium">{title}</p>

      {description === undefined ? null : (
        // `max-w-prose` rather than a fixed width: the sentences here are editor- and
        // developer-written and vary from four words to three lines.
        <p className="text-muted-foreground max-w-prose text-sm text-balance">{description}</p>
      )}

      {action === undefined ? null : <div className="mt-1">{action}</div>}
    </div>
  )
}
